// src/pages/admin/invoices/InvoiceList.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Stack
} from '@mui/material';
import { Search as SearchIcon, KeyboardArrowDown as ExpandMoreIcon, KeyboardArrowUp as ExpandLessIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router';
import MainCard from 'components/MainCard';
import StatisticsCards from 'components/StatisticsCards';
import InvoiceStatusChip from 'components/invoices/InvoiceStatusChip';
import ConfirmDialog from 'components/ConfirmDialog';
import TableActions from 'components/TableActions';
import invoiceService from 'services/invoiceService';
import projectService from 'services/projectService';
import { formatCurrency, formatDate } from 'utils/formatters';
import { useCurrency } from 'hooks/useCurrency';
import { useSnackbar } from 'hooks/useSnackbar';
import { getInvoiceStatusColor } from 'utils/statusHelpers';

const AdminInvoiceList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { defaultCurrency, convertCurrency, refreshCurrencySettings } = useCurrency();

  // Cache all invoices
  const [allInvoices, setAllInvoices] = useState([]);

  // Filtered and grouped data
  const [groupedInvoices, setGroupedInvoices] = useState([]);
  const [monthlySummaries, setMonthlySummaries] = useState({});
  const [loading, setLoading] = useState(true);
  const [totalInvoices, setTotalInvoices] = useState(0);

  // Expand/collapse state for month groups
  const [expandedMonths, setExpandedMonths] = useState({});

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');

  // Filter options
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, invoiceId: null });
  const { showSuccess, showError, snackbar, handleClose } = useSnackbar();

  useEffect(() => {
    fetchInvoices();
    fetchStatistics();
    fetchFilterData();
    fetchMonthlySummaries();
  }, []);

  // Refresh data when the page becomes visible or gains focus (e.g., after navigating back from settings)
  useEffect(() => {
    const handleRefresh = () => {
      refreshCurrencySettings(); // Refresh currency settings first
      fetchInvoices();
      fetchStatistics();
      fetchMonthlySummaries();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleRefresh();
      }
    };

    const handleFocus = () => {
      handleRefresh();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshCurrencySettings]);

  // Apply filters whenever filters change
  useEffect(() => {
    applyFilters();
    setPage(0); // Reset to first page when filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, projectFilter, clientFilter, vendorFilter, allInvoices]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await invoiceService.getInvoices({
        per_page: 'all'
      });
      setAllInvoices(response.data || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allInvoices];

    // Exclude draft invoices for admin view (now also handled by backend)
    filtered = filtered.filter((inv) => inv.status !== 'draft');

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((inv) => inv.status === statusFilter);
    }

    // Apply project filter
    if (projectFilter !== 'all') {
      filtered = filtered.filter((inv) => inv.project_id === parseInt(projectFilter));
    }

    // Apply client filter
    if (clientFilter !== 'all') {
      filtered = filtered.filter((inv) => inv.client_id === parseInt(clientFilter));
    }

    // Apply vendor filter
    if (vendorFilter !== 'all') {
      filtered = filtered.filter((inv) => inv.vendor_id === parseInt(vendorFilter));
    }

    // Apply search query
    if (search) {
      const query = search.toLowerCase();
      filtered = filtered.filter((inv) => {
        const invoiceNumber = inv.custom_invoice_number || inv.invoice_number;
        return (
          invoiceNumber?.toLowerCase().includes(query) ||
          inv.description?.toLowerCase().includes(query) ||
          inv.vendor?.company_name?.toLowerCase().includes(query) ||
          inv.client?.company_name?.toLowerCase().includes(query)
        );
      });
    }

    // Sort by invoice_date descending (newest first)
    filtered.sort((a, b) => new Date(b.invoice_date) - new Date(a.invoice_date));

    // Update total count
    setTotalInvoices(filtered.length);

    // Group the filtered invoices by billing_month for display
    const grouped = filtered.reduce((acc, invoice) => {
      const month = invoice.billing_month || 'no-month';
      if (!acc[month]) {
        acc[month] = [];
      }
      acc[month].push(invoice);
      return acc;
    }, {});

    // Convert to array format with aggregated data
    const groupedArray = Object.entries(grouped)
      .map(([month, invoices]) => ({
        billing_month: month,
        month_label:
          month !== 'no-month'
            ? new Date(month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
            : 'No billing month',
        invoice_count: invoices.length,
        total_amount: invoices.reduce((sum, inv) => {
          const amount = parseFloat(inv.amount || 0);
          const convertedAmount = convertCurrency(amount, inv.currency || 'USD', defaultCurrency);
          return sum + convertedAmount;
        }, 0),
        invoices: invoices.sort((a, b) => new Date(b.invoice_date) - new Date(a.invoice_date))
      }))
      .sort((a, b) => {
        // Sort by billing_month descending (newest first)
        if (a.billing_month === 'no-month') return 1;
        if (b.billing_month === 'no-month') return -1;
        return b.billing_month.localeCompare(a.billing_month);
      });

    setGroupedInvoices(groupedArray);

    // Auto-expand all months if not already set
    setExpandedMonths((prev) => {
      const newExpanded = { ...prev };
      groupedArray.forEach((group) => {
        if (!(group.billing_month in newExpanded)) {
          newExpanded[group.billing_month] = true;
        }
      });
      return newExpanded;
    });
  };

  const fetchStatistics = async () => {
    try {
      const stats = await invoiceService.getStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const fetchMonthlySummaries = async () => {
    try {
      const response = await invoiceService.getMonthlySummary();
      // Convert array to object keyed by billing_month for easy lookup
      const summariesMap = {};
      response.monthly_summaries?.forEach((summary) => {
        summariesMap[summary.billing_month] = summary;
      });
      setMonthlySummaries(summariesMap);
    } catch (error) {
      console.error('Failed to fetch monthly summaries:', error);
    }
  };

  const fetchFilterData = async () => {
    try {
      const [projectsRes, clientsRes, vendorsRes] = await Promise.all([
        projectService.getProjects({ per_page: 'all' }),
        projectService.getClients(),
        projectService.getVendors()
      ]);
      setProjects(projectsRes.data || []);
      setClients(clientsRes.data || []);
      setVendors(vendorsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch filter data:', error);
    }
  };

  const handleMarkAsPaid = async () => {
    try {
      await invoiceService.markAsPaid(confirmDialog.invoiceId, 'Marked as Remittance Advice.');
      fetchInvoices();
      fetchStatistics();
      fetchMonthlySummaries();
      setConfirmDialog({ open: false, invoiceId: null });
      showSuccess('Invoice marked as paid successfully');
    } catch (error) {
      console.error('Failed to mark invoice as paid:', error);
      showError('Failed to mark invoice as paid');
    }
  };

  const handleApprove = async () => {
    try {
      await invoiceService.approveInvoice(confirmDialog.invoiceId, 'Invoice approved.');
      fetchInvoices();
      fetchStatistics();
      setConfirmDialog({ open: false, invoiceId: null });
      showSuccess('Invoice approved successfully');
    } catch (error) {
      console.error('Failed to approve invoice:', error);
      showError('Failed to approve invoice.');
    }
  };

  const handleReject = async () => {
    try {
      await invoiceService.rejectInvoice(confirmDialog.invoiceId, 'Invoice rejected.');
      fetchInvoices();
      fetchStatistics();
      setConfirmDialog({ open: false, invoiceId: null });
      showSuccess('Invoice rejected successfully');
    } catch (error) {
      console.error('Failed to reject invoice:', error);
      showError('Failed to reject invoice.');
    }
  };

  const handleConfirmAction = () => {
    if (confirmDialog.type === 'approve') {
      handleApprove();
    } else if (confirmDialog.type === 'reject') {
      handleReject();
    } else if (confirmDialog.type === 'markAsPaid') {
      handleMarkAsPaid();
    }
  };

  const toggleMonthExpansion = (billingMonth) => {
    setExpandedMonths((prev) => ({
      ...prev,
      [billingMonth]: !prev[billingMonth]
    }));
  };

  // Helper function to render a single invoice row
  const renderInvoiceRow = (invoice) => (
    <TableRow key={invoice.id} hover>
      <TableCell>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 500
          }}
        >
          {invoice.custom_invoice_number || invoice.invoice_number}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{invoice.vendor?.company_name || '-'}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{invoice.client?.company_name || '-'}</Typography>
      </TableCell>
      <TableCell>{invoice.project?.name || '-'}</TableCell>
      <TableCell>{formatDate(invoice.due_date)}</TableCell>
      <TableCell align="right">
        {invoice.billing_units ? (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500
            }}
          >
            {invoice.billing_units} {invoice.billing_unit_type === 'hours' ? 'hrs' : invoice.billing_unit_type}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary'
            }}
          >
            -
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">{formatCurrency(invoice.amount, invoice.currency)}</TableCell>
      <TableCell>
        <InvoiceStatusChip status={invoice.status} />
      </TableCell>
      <TableCell align="center">
        <TableActions
          onView={() => navigate(`/admin/invoices/${invoice.id}`)}
          onPay={invoice.status === 'approved' ? () => setConfirmDialog({ open: true, type: 'markAsPaid', invoiceId: invoice.id }) : null}
          onApprove={invoice.status === 'submitted' ? () => setConfirmDialog({ open: true, type: 'approve', invoiceId: invoice.id }) : null}
          onReject={invoice.status === 'submitted' ? () => setConfirmDialog({ open: true, type: 'reject', invoiceId: invoice.id }) : null}
        />
      </TableCell>
    </TableRow>
  );

  const statisticsData = statistics
    ? [
        { label: 'Total', value: statistics.total - (statistics.draft || 0) },
        { label: 'Submitted', value: statistics.submitted, color: `${getInvoiceStatusColor('submitted')}.main` },
        { label: 'Approved', value: statistics.approved, color: `${getInvoiceStatusColor('approved')}.main` },
        { label: 'Total Paid', value: formatCurrency(statistics.paid_amount, statistics.currency || defaultCurrency) },
        {
          label: 'Total Open',
          value: formatCurrency(statistics.approved_amount + statistics.pending_amount, statistics.currency || defaultCurrency)
        }
      ]
    : [];

  return (
    <Box>
      {/* Statistics Cards */}
      <StatisticsCards statistics={statisticsData} loading={!statistics} columns={5} />

      <MainCard title="All Invoices">
        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, ms: 6 }}>
            <TextField
              fullWidth
              placeholder="Search invoices..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, ms: 6, md: 3 }}>
            <TextField fullWidth select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="submitted">Submitted</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="paid">Remittance Advice</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, ms: 6, md: 3 }}>
            <TextField fullWidth select label="Project" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
              <MenuItem value="all">All Projects</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, ms: 6, md: 3 }}>
            <TextField fullWidth select label="Client" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
              <MenuItem value="all">All Clients</MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.company_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, ms: 6, md: 3 }}>
            <TextField fullWidth select label="Vendor" value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)}>
              <MenuItem value="all">All Vendors</MenuItem>
              {vendors.map((vendor) => (
                <MenuItem key={vendor.id} value={vendor.id}>
                  {vendor.company_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Due date</TableCell>
                    <TableCell align="right">Hours</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {totalInvoices === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography variant="body1" sx={{ py: 3 }}>
                          No invoices found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    // Show paginated grouped invoices by month
                    <>
                      {(() => {
                        // Flatten all invoices from all groups for pagination
                        const allInvoicesFlat = groupedInvoices.flatMap((group) =>
                          group.invoices.map((invoice) => ({ ...invoice, groupMonth: group.billing_month, groupLabel: group.month_label }))
                        );

                        // Paginate the flattened list
                        const startIndex = page * rowsPerPage;
                        const endIndex = startIndex + rowsPerPage;
                        const paginatedInvoices = allInvoicesFlat.slice(startIndex, endIndex);

                        // Group paginated invoices back by month
                        const paginatedGroups = paginatedInvoices.reduce((acc, invoice) => {
                          const month = invoice.groupMonth;
                          if (!acc[month]) {
                            acc[month] = {
                              billing_month: month,
                              month_label: invoice.groupLabel,
                              invoices: []
                            };
                          }
                          acc[month].invoices.push(invoice);
                          return acc;
                        }, {});

                        return Object.values(paginatedGroups).map((group) => (
                          <React.Fragment key={group.billing_month}>
                            {/* Month Header Row */}
                            <TableRow
                              sx={{
                                bgcolor: 'action.hover',
                                '&:hover': { bgcolor: 'action.selected' },
                                cursor: 'pointer'
                              }}
                              onClick={() => toggleMonthExpansion(group.billing_month)}
                            >
                              <TableCell colSpan={9}>
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  sx={{
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={2}
                                    sx={{
                                      alignItems: 'center'
                                    }}
                                  >
                                    <IconButton size="small">
                                      {expandedMonths[group.billing_month] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: 600
                                      }}
                                    >
                                      {group.month_label}
                                    </Typography>
                                  </Stack>

                                  {/* Financial Summary */}
                                  {monthlySummaries[group.billing_month] && (
                                    <Stack direction="row" spacing={4} sx={{ mr: 2 }}>
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: 'text.secondary',
                                            display: 'block'
                                          }}
                                        >
                                          Total Costs
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 600,
                                            color: 'warning.main'
                                          }}
                                        >
                                          {formatCurrency(monthlySummaries[group.billing_month].total_costs, defaultCurrency)}
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: 'text.secondary',
                                            display: 'block'
                                          }}
                                        >
                                          Projected Revenue
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 600,
                                            color: 'success.main'
                                          }}
                                        >
                                          {formatCurrency(monthlySummaries[group.billing_month].projected_revenue, defaultCurrency)}
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: 'text.secondary',
                                            display: 'block'
                                          }}
                                        >
                                          Margin
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color={
                                            monthlySummaries[group.billing_month].margin_percentage >= 0 ? 'success.main' : 'error.main'
                                          }
                                          sx={{
                                            fontWeight: 600
                                          }}
                                        >
                                          {monthlySummaries[group.billing_month].margin_percentage.toFixed(1)}%
                                        </Typography>
                                      </Box>
                                    </Stack>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                            {/* Collapsible Invoice Rows */}
                            {expandedMonths[group.billing_month] && group.invoices.map((invoice) => renderInvoiceRow(invoice))}
                          </React.Fragment>
                        ));
                      })()}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalInvoices}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </MainCard>

      <ConfirmDialog
        open={confirmDialog.open}
        type={confirmDialog.type}
        itemName="invoice"
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmDialog({ open: false, type: null, invoiceId: null })}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminInvoiceList;
